import React, { useCallback, useMemo, useRef, useState } from "react";
import {
	Button,
	Dropdown,
	Form,
	Grid,
	Header,
	Icon,
	Message,
	Modal,
	Segment,
	SegmentGroup,
	Table,
} from "semantic-ui-react";
import type { DropdownProps } from "semantic-ui-react";
import Map, { Layer, MapRef, Source } from "react-map-gl/mapbox";
import { bbox } from "@turf/turf";
import Tooltip from "rc-tooltip";
import _ from "lodash";

import {
	type DataLayerType,
	type PreprocessedFeatureCollectionProps,
	type UUID,
	type VectorDataLayerValuesMapping,
	type VectorDataLayerUploadPreprocResult,
	type VectorDataLayerUploadStdResult,
	type VectorDataLayerRequest,
	type DataLayerUpload,
	type StdFeatureCollectionProps,
	type StdFeatureProps,
	type AttributeTableValue,
	DataType,
} from "../../../../apis/types/dataLayerTypes";
import { toast } from "react-toastify";
import { useMutation } from "@tanstack/react-query";
import { ApiError, updateVectorDataLayerUpload } from "../../../../apis/dataLayerApi";
import { useAuth0 } from "../../../../auth/auth0";
import { useUserAuth } from "../../../../hooks/useUserAuth";
import { AerialPlotResponse } from "../../../../apis/types";
import { DataDrivenPropertyValueSpecification } from "mapbox-gl";
import { formatInt, maxDecimalPlaces } from "../../../../utilities/fmt";
import { M2_TO_ACRES } from "../../../../utilities/constants";
import {
	CATEGORICAL_PALETTE,
	FIELD_BOUNDARY_CSS_VAR,
	FIELD_BOUNDARY_FALLBACK_COLOR,
	TRANSPARENT_COLOR,
	generateGreyscalePalette,
	makeFeatureFillLayerProps,
	makeFeatureOutlineLayerProps,
	makeValueLabelLayerProps,
	resolveCssVarColor,
} from "../../../../utilities/graphics";
import SimpleOkCancelModal from "../../../Lumber/SimpleOkCancelModal";
import { useFieldAssociationConfirmation } from "../../../../hooks/useFieldAssociationConfirmation";

import { FIELD_ASSOC_RESET_HEADER, FIELD_ASSOC_RESET_BODY } from "./constants";
import styles from "./styles.module.css";

export interface VectorConfiguratorModalProps {
	upload: DataLayerUpload; // DataLayerUploads record for tracking state
	existingUploads: DataLayerUpload[]; // existing, for validation
	// preProcessedGeoJson: Fetched UrlPath GeoJSON with intermediate feature collection metadata props (same as response from POST upload)
	preProcessedGeoJson: VectorDataLayerUploadPreprocResult;
	// stdGeoJson: Fetched PreprocessedDataUrlPath GeoJSON with aerialFARMS standardized metadata props.
	stdGeoJson?: VectorDataLayerUploadStdResult;
	vectorDataType: DataLayerType; // detail of upload's vector data type
	dataLayerHasExistingFieldAssociations: boolean;
	onResetExistingFieldAssociations: () => void;
	onSaved: (uploadId: UUID, response: AerialPlotResponse<VectorDataLayerUploadPreprocResult>) => void; // called when Save request is completed
	onClose: () => void; // called when Close button pressed
	onCancel: () => void; // called when Cancel button pressed
}

/**
 * Maximum number of categories the UI flow is supporting.
 */
const MAX_ZONES = 12;

/**
 * Number of source attribute columns to preview by default at load.
 */
const NUM_COLS_DEFAULT = 3;

/**
 * Reserved value for system (must have description "unassigned")
 */
const UNASSIGNED_ID = 0;
const UNASSIGNED_DESCR = "unassigned";
const DEFAULT_ATTR_VALUE_DESCR_OPTIONS = [
	{
		key: UNASSIGNED_DESCR,
		value: UNASSIGNED_DESCR,
		text: UNASSIGNED_DESCR,
	},
];

/**
 * Dropdown menu options for mapbox overlay.
 */
const MAP_VIS_ATTR_TARGET_OPTIONS = [
	{
		key: "raw",
		text: "Source (raw) attributes",
		value: "raw",
	},
	{
		key: "std",
		text: "Assigned (aerialFARMS) attributes",
		value: "std",
	},
];

/**
 * Only supporting 1 layer currently (from a raw vector data file)
 */
const LAYER_NUM = 0;

// Preset descriptions for quantitative zone assignments, keyed by zone count (2–7).
// Index 0 is zone ID 1, index 1 is zone ID 2, etc.
const AUTO_FILL_DESCRIPTIONS: Record<number, string[]> = {
	2: ["Low", "High"],
	3: ["Low", "Medium", "High"],
	4: ["Low", "Medium", "High", "Very High"],
	5: ["Low", "Low-medium", "Medium", "Medium-High", "High"],
	6: ["Low", "Low-medium", "Medium", "Medium-High", "High", "Very High"],
	7: ["Very Low", "Low", "Low-medium", "Medium", "Medium-High", "High", "Very High"],
};

/**
 * Number of features to preview in Attribute values from data source table.
 */
const PREVIEW_TOP_N_FEATURES = 100;

const ColorChip = ({ bgColor }: { bgColor: string }) => (
	<div style={{ backgroundColor: bgColor }} className={styles.colorChip} />
);

const TransparentColorChip = () => <div className={`${styles.colorChip} ${styles.checkerboard}`} />;

/**
 * Enum for switching between attribute table visualizations on mapbox.
 */
enum AttrVis {
	RAW = "raw",
	STD = "std",
}

/**
 * Enum of columns in the configure attribute columns table.
 */
enum ConfigAttrTableColumn {
	SRC_VALS = "srcVals",
	STD_IDS = "stdIds",
	STD_DESCR = "stdDescr",
	STD_VALUE = "stdValue",
	STD_AREA = "stdArea",
}

/**
 * Which section to hi-light if there is a validation error. Errors are generated one at a time.
 */
export enum ValidationSection {
	IdColumn = 0,
	DescriptionColumn = 1,
	SelectCategoryNumber = 2,
	AttributeColumn = 3,
}

/**
 * Data class for the attribute table editor / data grid.
 */
interface AttributeTableRecord {
	/**
	 * Raw attribute table value.
	 */
	attributeTableValue: AttributeTableValue;
	/**
	 * aerialFARMS standard ID integer [0, numZones]
	 */
	stdId: number;
	/**
	 * Custom description of category (optional)
	 */
	catDesc?: string;
	/**
	 * Derived value of category, is persisted regardless of property `catDesc`.
	 */
	catValue: AttributeTableValue;
	/**
	 * Derived area in acres from property `areaM2`.
	 */
	areaAcres: number;
	/**
	 * During editing if there is a feature dissolve required, estimated values will be displayed.
	 */
	valueIsEstimate: boolean;
	areaIsEstimate: boolean;
}

/**
 * Per-zone aggregated display values (Value and Acres columns).
 * Keyed by stdId. Derived from attrTableRecords + raw feature data.
 */
interface ZoneDisplayValues {
	value: string;
	valueIsEstimate: boolean;
	areaAcres: number;
	areaIsEstimate: boolean;
	/** Per-attributeTableValue count of source GeoJSON features (stringified key). */
	featureCountByAttrValue: Record<string, number>;
	/** Number of distinct attribute values mapped to this stdId (>1 means same ID selected for multiple rows). */
	mappedAttrValueCount: number;
}

/**
 * Vector Data Layer Upload configurator.
 * Wraps semantic-ui's Modal. Interaction is strict: forces user to pick Save/Apply or Cancel.
 */
const VectorConfiguratorModal = ({
	upload,
	existingUploads,
	preProcessedGeoJson,
	stdGeoJson,
	vectorDataType,
	dataLayerHasExistingFieldAssociations,
	onResetExistingFieldAssociations,
	onSaved,
	onCancel,
}: VectorConfiguratorModalProps) => {
	const mapRef = useRef<MapRef>(null);
	const { getTokenSilently } = useAuth0() as { getTokenSilently: () => Promise<string> };
	const userAuth = useUserAuth() as { currentClientId: string };

	const {
		fieldAssocModalIsOpen,
		handleFieldAssocModalOk,
		handleFieldAssocModalCancel,
		withFieldAssociationConfirmation,
	} = useFieldAssociationConfirmation(dataLayerHasExistingFieldAssociations, onResetExistingFieldAssociations);

	const preprocessedLayerMetadata = (
		preProcessedGeoJson as unknown as { properties: PreprocessedFeatureCollectionProps }
	).properties;

	/*
	 * Pre-State setup -- compute derived values
	 */

	// Compute initial selectedAttrCol from props (used by multiple state initializers)
	const initialSelectedAttrCol = (() => {
		if (stdGeoJson !== undefined) {
			const stdLayerMetadata = (stdGeoJson as unknown as { properties: StdFeatureCollectionProps }).properties;
			return stdLayerMetadata.rawAttributeFieldName;
		}
		const layerMetadata = (preProcessedGeoJson as unknown as { properties: PreprocessedFeatureCollectionProps })
			.properties;
		return layerMetadata.attributeTables![LAYER_NUM].fields.fields[0];
	})();

	// Sorted unique attribute values for a given column
	const getSortedUniqueAttrVals = useCallback(
		(attrCol: string): AttributeTableValue[] =>
			_.sortedUniq(
				_.sortBy(
					preProcessedGeoJson.features.map((feature): AttributeTableValue => {
						if (feature.properties) {
							return feature.properties[attrCol] as AttributeTableValue;
						}
						return false;
					}),
				),
			),
		[preProcessedGeoJson.features],
	);

	// Returns true if the given column holds numeric (quantitative) data
	const isQuantitativeCol = useCallback(
		(attrCol: string): boolean => {
			const fields = preprocessedLayerMetadata.attributeTables?.[LAYER_NUM].fields;
			if (!fields) return false;
			const idx = fields.fields.indexOf(attrCol);
			if (idx === -1) return false;
			const dt = fields.dataTypes[idx];
			return dt === DataType.INT || dt === DataType.FLOAT;
		},
		[preprocessedLayerMetadata],
	);

	// Max decimal places found across all features for a given numeric attribute column.
	// Keyed by column name so the preview table can format any visible column.
	const colDecimalsMap = useMemo(() => {
		const map: Record<string, number> = {};
		const attrTableMeta = preprocessedLayerMetadata.attributeTables?.[LAYER_NUM].fields;
		if (!attrTableMeta) return map;
		for (const { col, dt } of _.zipWith(attrTableMeta.fields, attrTableMeta.dataTypes, (col, dt) => ({
			col,
			dt,
		}))) {
			if (dt !== DataType.INT && dt !== DataType.FLOAT) continue;
			map[col] = maxDecimalPlaces(
				preProcessedGeoJson.features.map((f) => {
					const v: unknown = f.properties?.[col];
					return typeof v === "number" ? v : 0;
				}),
			);
		}
		return map;
	}, [preprocessedLayerMetadata, preProcessedGeoJson.features]);

	// Maps column name → whether nulls were detected in that column.
	const colNullsMap = useMemo(() => {
		const map: Record<string, boolean> = {};
		const attrTableMeta = preprocessedLayerMetadata.attributeTables?.[LAYER_NUM].fields;
		if (!attrTableMeta) return map;
		for (const { col, hasNulls } of _.zipWith(attrTableMeta.fields, attrTableMeta.nullsDetected, (col, hasNulls) => ({
			col,
			hasNulls,
		}))) {
			map[col] = hasNulls;
		}
		return map;
	}, [preprocessedLayerMetadata]);

	// Builds AttributeTableRecord[] from sorted unique attribute values for the given column.
	// Used both at init time and when selectedAttrCol changes.
	const buildAttrTableRecords = useCallback(
		(attrCol: string, initNumZones: number, dissolve: boolean): AttributeTableRecord[] => {
			const sortedUniqueAttrVals = getSortedUniqueAttrVals(attrCol);
			const isQuant = isQuantitativeCol(attrCol);

			// group features by their value in attrCol for area/value computations
			const featuresByVal = _.groupBy(preProcessedGeoJson.features, (f) => f.properties?.[attrCol] ?? false);

			return sortedUniqueAttrVals.map((attrVal, i): AttributeTableRecord => {
				const id = i < initNumZones ? i + 1 : UNASSIGNED_ID;
				const desc = id === UNASSIGNED_ID ? UNASSIGNED_DESCR : undefined;

				const matchingFeatures = featuresByVal[String(attrVal)] ?? [];
				const totalAreaM2 = _.sumBy(matchingFeatures, (f) => (f.properties?.areaM2 as number) ?? 0);

				let value: AttributeTableValue = attrVal;
				let valueIsEstimate = false;
				if (dissolve && isQuant) {
					const weightedSum = _.sumBy(
						matchingFeatures,
						(f) => ((f.properties?.[attrCol] as number) ?? 0) * ((f.properties?.areaM2 as number) ?? 0),
					);
					value = totalAreaM2 > 0 ? weightedSum / totalAreaM2 : attrVal;
					valueIsEstimate = true;
				}

				return {
					attributeTableValue: attrVal,
					stdId: id,
					catDesc: desc,
					catValue: value,
					valueIsEstimate: valueIsEstimate,
					areaAcres: totalAreaM2 * M2_TO_ACRES,
					areaIsEstimate: dissolve,
				};
			});
		},
		[getSortedUniqueAttrVals, isQuantitativeCol, preProcessedGeoJson.features],
	);

	/*
	 * State
	 */

	// number of categories or zones user is targeting
	const [numZones, setNumZones] = useState<number>(() => {
		if (stdGeoJson !== undefined) {
			const stdLayerMetadata = (stdGeoJson as unknown as { properties: StdFeatureCollectionProps }).properties;
			return stdLayerMetadata.attributeDescriptions.length - 1;
		}
		const sortedUniqueAttrVals = getSortedUniqueAttrVals(initialSelectedAttrCol);
		return _.min([MAX_ZONES, sortedUniqueAttrVals.length]) ?? 1;
	});

	// attribute columns to display in source data table
	const [selectedSourceAttrCols, setSelectedSourceAttrCols] = useState<string[]>(() => {
		if (stdGeoJson !== undefined) {
			const stdLayerMetadata = (stdGeoJson as unknown as { properties: StdFeatureCollectionProps }).properties;
			return [stdLayerMetadata.rawAttributeFieldName];
		}
		const layerMetadata = (preProcessedGeoJson as unknown as { properties: PreprocessedFeatureCollectionProps })
			.properties;
		const allFields = layerMetadata.attributeTables![LAYER_NUM].fields.fields;
		return [...allFields].slice(0, NUM_COLS_DEFAULT);
	});

	// selected attribute column aka attribute field (hilited column in source attrs data table)
	const [selectedAttrCol, setSelectedAttrCol] = useState<string>(initialSelectedAttrCol);

	// true while attrTableRecords still reflects the stdGeoJson exact values (no user zone edits yet)
	const [stdGeoJsonUnedited, setStdGeoJsonUnedited] = useState(stdGeoJson !== undefined);

	// primary state for the config attribute table — drives both UI and (via memo) the API payload
	const [attrTableRecords, setAttrTableRecords] = useState<AttributeTableRecord[]>(() => {
		if (stdGeoJson !== undefined) {
			// in a round-trip edit use case, begin with the standard geojson from API (is authoritative)
			const stdLayerMetadata = (stdGeoJson as unknown as { properties: StdFeatureCollectionProps }).properties;
			return stdLayerMetadata.rawValuesMapping.map(([attrVal, stdId]): AttributeTableRecord => {
				const stdFeature = stdGeoJson.features.find((f) => (f.properties as StdFeatureProps).aerialfarmsStd === stdId);
				const stdProps = (stdFeature?.properties as StdFeatureProps | null) ?? undefined;
				const stdValue: AttributeTableValue = stdProps?.catValueNum ?? stdProps?.catValueText ?? attrVal;
				const stdDescr = stdProps?.catDesc;
				const stdArea = stdProps ? stdProps.areaM2 * M2_TO_ACRES : 0;
				return {
					attributeTableValue: attrVal,
					stdId,
					catDesc: stdDescr,
					catValue: stdValue,
					valueIsEstimate: false,
					areaAcres: stdArea,
					areaIsEstimate: false,
				};
			});
		}
		const sortedUniqueAttrVals = getSortedUniqueAttrVals(initialSelectedAttrCol);
		// numZones and willDissolveFeatures aren't readable here, so recompute them
		const initNumZones = _.min([MAX_ZONES, sortedUniqueAttrVals.length]) ?? 1;
		const initWillDissolve = preProcessedGeoJson.features.length !== initNumZones;
		return buildAttrTableRecords(initialSelectedAttrCol, initNumZones, initWillDissolve);
	});

	// is PUT mutation in progress
	const [isSaving, setIsSaving] = useState<boolean>(false);

	// prevent user from always seeing validation message from moment modal is opened
	const [isValidationMsgShown, setIsValidationMsgShown] = useState<boolean>(false);

	// track if Save is needed:
	//   - initial upload workflow: allow immediate save and let API fill in descriptions for each zone.
	//   - round-trip upload workflow: don't allow immediate save, require edits first before save.
	// note: in the round-trip case, this is not a strict dirty check because when we have StdGeoJson
	// file, we don't compare the next state with what was in the previously saved StdGeoJson.
	const [formIsDirty, setFormIsDirty] = useState<boolean>(() => {
		return stdGeoJson ? false : true;
	});

	// column sorting flags - preview attribute table
	const [previewAttrTableSortColumn, setPreviewAttrTableSortColumn] = useState<string | undefined>(undefined);
	const [previewAttrTableSortColumnDir, setPreviewAttrTableSortColumnDir] = useState<boolean>(false);

	// column sorting flags - assign std attribute values table
	const [configAttrTableSortColumn, setConfigAttrTableSortColumn] = useState<ConfigAttrTableColumn>(
		ConfigAttrTableColumn.STD_VALUE,
	);
	const [configAttrTableSortColumnDir, setConfigAttrTableSortColumnDir] = useState<boolean>(false);

	// user selection of which attribute table to visualize on mapbox
	const [mapAttributesTarget, setMapAttributesTarget] = useState<AttrVis>(AttrVis.STD);

	/*
	 * Queries and Mutations
	 */
	const { mutateAsync: updateVectorDataLayerUploadMutationAsync } = useMutation({
		mutationFn: updateVectorDataLayerUpload,
		onSettled: () => {
			setIsSaving(false);
		},
	});

	/*
	 * Memoization
	 */

	// Derive API payload from records — all downstream consumers unchanged
	const valuesMapping = useMemo<VectorDataLayerValuesMapping[]>(
		() =>
			attrTableRecords.map(({ attributeTableValue, stdId, catDesc: stdDesc }) => [
				attributeTableValue,
				stdId,
				stdDesc ?? null,
			]),
		[attrTableRecords],
	);

	// Per-zone aggregated Value and Acres, keyed by stdId.
	// Value = concatenated source attribute values for that zone.
	// Acres = sum of area for all features assigned to that zone.
	const zoneDisplayValues = useMemo(() => {
		const result: Record<number, ZoneDisplayValues> = {};

		// stdGeoJson path (unedited): values are exact (already dissolved by API), read directly from records.
		if (stdGeoJsonUnedited) {
			const colDecimals = colDecimalsMap[selectedAttrCol] ?? 0;
			const byId = _.groupBy(attrTableRecords, (r) => r.stdId);
			for (const [idStr, recs] of Object.entries(byId)) {
				const id = Number(idStr);
				const firstRec = recs[0];
				const stdValueStr =
					typeof firstRec.catValue === "number" ? firstRec.catValue.toFixed(colDecimals) : String(firstRec.catValue);
				result[id] = {
					value: stdValueStr,
					valueIsEstimate: false,
					areaAcres: firstRec.areaAcres, // display API calculated area for the dissolved zones, per id.
					areaIsEstimate: false,
					featureCountByAttrValue: Object.fromEntries(recs.map((r) => [String(r.attributeTableValue), 1])),
					mappedAttrValueCount: recs.length,
				};
			}
			return result;
		}

		const isQuant = isQuantitativeCol(selectedAttrCol);

		const byId = _.groupBy(attrTableRecords, (r) => r.stdId);

		for (const [idStr, recs] of Object.entries(byId)) {
			const id = Number(idStr);

			const concatenated = recs.map((r) => String(r.attributeTableValue)).join("+");

			let totalAreaM2 = 0;
			let totalFeatureCount = 0;
			const featureCountByAttrValue: Record<string, number> = {};
			for (const rec of recs) {
				const matchingFeatures = preProcessedGeoJson.features.filter(
					(f) => f.properties?.[selectedAttrCol] === rec.attributeTableValue,
				);
				featureCountByAttrValue[String(rec.attributeTableValue)] = matchingFeatures.length;
				totalFeatureCount += matchingFeatures.length;
				totalAreaM2 += _.sumBy(matchingFeatures, (f) => (f.properties?.areaM2 as number) ?? 0);
			}
			const totalAcres = totalAreaM2 * M2_TO_ACRES;
			const zoneIsDissolving = totalFeatureCount > 1;

			let stdValue: string;
			let stdValueIsEstimate = false;
			if (zoneIsDissolving && isQuant) {
				let weightedSum = 0;
				let weightTotal = 0;
				for (const rec of recs) {
					const matchingFeatures = preProcessedGeoJson.features.filter(
						(f) => f.properties?.[selectedAttrCol] === rec.attributeTableValue,
					);
					for (const f of matchingFeatures) {
						const val = (f.properties?.[selectedAttrCol] as number) ?? 0;
						const area = (f.properties?.areaM2 as number) ?? 0;
						weightedSum += val * area;
						weightTotal += area;
					}
				}
				const colDecimals = colDecimalsMap[selectedAttrCol] ?? 2;
				stdValue = weightTotal > 0 ? (weightedSum / weightTotal).toFixed(colDecimals) : concatenated;
				stdValueIsEstimate = true;
			} else if (isQuant && recs.length === 1) {
				const colDecimals = colDecimalsMap[selectedAttrCol] ?? 0;
				const val = recs[0].attributeTableValue;
				stdValue = typeof val === "number" ? val.toFixed(colDecimals) : concatenated;
			} else {
				stdValue = concatenated;
			}

			result[id] = {
				value: stdValue,
				valueIsEstimate: stdValueIsEstimate,
				areaAcres: totalAcres,
				areaIsEstimate: zoneIsDissolving,
				featureCountByAttrValue,
				mappedAttrValueCount: recs.length,
			};
		}

		return result;
	}, [
		attrTableRecords,
		selectedAttrCol,
		isQuantitativeCol,
		preProcessedGeoJson.features,
		stdGeoJsonUnedited,
		colDecimalsMap,
	]);

	const selectedAttrColIsQuant = useMemo(
		() => isQuantitativeCol(selectedAttrCol),
		[selectedAttrCol, isQuantitativeCol],
	);

	const hasEstimatedValues = useMemo(
		() => Object.values(zoneDisplayValues).some((z) => z.valueIsEstimate),
		[zoneDisplayValues],
	);

	const hasEstimatedAreas = useMemo(
		() => Object.values(zoneDisplayValues).some((z) => z.areaIsEstimate),
		[zoneDisplayValues],
	);

	const sortedAttrTableRecords = useMemo(() => {
		// Compute the description dropdown placeholder for each record.
		// Round-trip records (stdGeoJsonUnedited or stdGeoJson path) never show a placeholder —
		// the real values are already in stdValue. For fresh-upload records, show the zone's
		// concatenated/estimated value from zoneDisplayValues when no stdDesc is set.
		const withPlaceholder = attrTableRecords.map((rec) => {
			let descPlaceholder: string | undefined;
			let estimateTooltip: string | undefined;
			const zone = zoneDisplayValues[rec.stdId];
			if (zone) {
				if (!rec.catDesc) {
					descPlaceholder = zone.valueIsEstimate ? `~${zone.value}` : zone.value;
				}
				if (zone.valueIsEstimate || zone.areaIsEstimate) {
					const parts: string[] = [];
					const featureCount = zone.featureCountByAttrValue[String(rec.attributeTableValue)] ?? 0;
					if (featureCount > 1) {
						parts.push(`Attribute value appears in ${featureCount} source features.`);
					}
					if (zone.mappedAttrValueCount > 1) {
						parts.push(`ID ${rec.stdId} is selected for ${zone.mappedAttrValueCount} attribute values.`);
					}
					estimateTooltip = parts.join(" ");
				}
			}
			return { ...rec, descPlaceholder, estimateTooltip };
		});

		let sorted: (AttributeTableRecord & { descPlaceholder: string | undefined; estimateTooltip: string | undefined })[];

		if (configAttrTableSortColumn === ConfigAttrTableColumn.SRC_VALS) {
			sorted = _.sortBy(withPlaceholder, (r) => r.attributeTableValue);
		} else if (configAttrTableSortColumn === ConfigAttrTableColumn.STD_IDS) {
			sorted = _.sortBy(withPlaceholder, (r) => r.stdId);
		} else if (configAttrTableSortColumn === ConfigAttrTableColumn.STD_DESCR) {
			sorted = _.sortBy(withPlaceholder, (r) => r.catDesc ?? r.descPlaceholder);
		} else if (configAttrTableSortColumn === ConfigAttrTableColumn.STD_VALUE) {
			sorted = _.sortBy(withPlaceholder, (r) => {
				const zone = zoneDisplayValues[r.stdId];
				if (!zone) return r.catValue;
				const n = Number(zone.value);
				return Number.isNaN(n) ? zone.value : n;
			});
		} else if (configAttrTableSortColumn === ConfigAttrTableColumn.STD_AREA) {
			sorted = _.sortBy(withPlaceholder, (r) => zoneDisplayValues[r.stdId]?.areaAcres ?? r.areaAcres);
		} else {
			sorted = [...withPlaceholder];
		}
		if (configAttrTableSortColumnDir) {
			sorted.reverse();
		}
		return sorted;
	}, [attrTableRecords, configAttrTableSortColumn, configAttrTableSortColumnDir, zoneDisplayValues]);

	// Greyscale color map keyed by stdId. Unassigned (id 0) → transparent.
	// Quantitative columns: palette position reflects sorted numeric value (low=dark, high=light).
	// Nominal columns: palette position assigned by zone id order.
	const targetAttrColors = useMemo(() => {
		const result: Record<number, string> = { [UNASSIGNED_ID]: TRANSPARENT_COLOR };
		if (numZones === 0) return result;
		const palette = generateGreyscalePalette(numZones);
		const assignedIds = Array.from({ length: numZones }, (_, i) => i + 1);
		if (isQuantitativeCol(selectedAttrCol)) {
			const numericValues: { id: number; val: number }[] = [];
			for (const id of assignedIds) {
				const n = Number(zoneDisplayValues[id]?.value);
				if (!Number.isNaN(n)) numericValues.push({ id, val: n });
			}
			if (numericValues.length > 0) {
				const sorted = [...numericValues].sort((a, b) => a.val - b.val);
				sorted.forEach(({ id }, i) => {
					result[id] = palette[Math.round((i / (sorted.length - 1 || 1)) * (palette.length - 1))];
				});
				// Any id with no parseable value falls back to palette by position.
				const assigned = new Set(numericValues.map((x) => x.id));
				assignedIds
					.filter((id) => !assigned.has(id))
					.forEach((id, i) => {
						result[id] = palette[i % palette.length];
					});
				return result;
			}
		}
		// Nominal or no values yet: assign palette by zone id order.
		assignedIds.forEach((id, i) => {
			result[id] = palette[i];
		});
		return result;
	}, [numZones, selectedAttrCol, isQuantitativeCol, zoneDisplayValues]);

	// List of categorical colors for the source feature table.
	const sourceFeatureColors = useMemo(() => {
		const numColors = _.min([preProcessedGeoJson.features.length, MAX_ZONES]) ?? 0;
		return preProcessedGeoJson.features.map((_, i) => {
			if (i >= numColors || i >= CATEGORICAL_PALETTE.length) {
				return "grey";
			}
			return CATEGORICAL_PALETTE[i];
		});
	}, [preProcessedGeoJson]);

	// Generate preprocessed GeoJson data, decorating the feature properties with attributes for visualization
	// for preview color of raw/source attribute table, just arbitrarily assign categorical colors
	// for preview colors of std/normal attribute table, grey-scales are assigned user-specified zone assignments
	const previewGeoJson = useMemo(() => {
		const stdIdLookup: Record<string | number, number> = {};
		valuesMapping.forEach(([srcVal, stdId]) => {
			if (typeof srcVal === "boolean") {
				stdIdLookup[String(srcVal)] = stdId;
			} else {
				stdIdLookup[srcVal] = stdId;
			}
		});
		const features = preProcessedGeoJson.features.map((feature, i) => {
			const previewAttrsColorIdx = i;
			const srcVal = feature.properties![selectedAttrCol] as string | number;
			const aerialfarmsStdId = stdIdLookup[srcVal] ?? 1;
			const zone = zoneDisplayValues[aerialfarmsStdId];
			const zoneValueLabel = zone ? (zone.valueIsEstimate ? `~${zone.value}` : zone.value) : "";
			return {
				...feature,
				properties: {
					...feature.properties,
					previewAttrsColorIdx,
					aerialfarmsStd: aerialfarmsStdId,
					zoneValueLabel,
				} as Record<string, unknown> & { previewAttrsColorIdx: number; aerialfarmsStd: number; zoneValueLabel: string },
			};
		});
		return {
			...preProcessedGeoJson,
			features,
		};
	}, [preProcessedGeoJson, selectedAttrCol, valuesMapping, zoneDisplayValues]);

	// for performance, don't display unlimited features in the source attributes table
	const topNGeoJsonFeatures = useMemo(() => {
		const features = previewGeoJson.features.slice(0, PREVIEW_TOP_N_FEATURES);
		if (!previewAttrTableSortColumn) {
			return features;
		}
		const sorted = _.sortBy(features, (f) => f.properties[previewAttrTableSortColumn]);
		if (previewAttrTableSortColumnDir) {
			sorted.reverse();
		}
		return sorted;
	}, [previewGeoJson, previewAttrTableSortColumn, previewAttrTableSortColumnDir]);

	// display a note if the list of features is truncated
	const topNGeoJsonFeaturesDescr = useMemo(() => {
		return topNGeoJsonFeatures.length === preProcessedGeoJson.features.length
			? ""
			: ` (Top ${PREVIEW_TOP_N_FEATURES} Features)`;
	}, [topNGeoJsonFeatures, preProcessedGeoJson.features.length]);

	const vectorDataTypeLabel = useMemo(() => {
		if (vectorDataType.presetName) {
			return vectorDataType.presetName;
		}
		return vectorDataType.name;
	}, [vectorDataType]);

	// Options data modal for DDL configuring source attributes data table.
	const sourceAttrColsSelectOptions = useMemo(() => {
		const layerMetadata = (preProcessedGeoJson as unknown as { properties: PreprocessedFeatureCollectionProps })
			.properties;
		const selectOptions = layerMetadata.attributeTables![LAYER_NUM].fields.fields.map((attrCol, i) => ({
			key: `${attrCol}-${i}`,
			value: attrCol,
			text: attrCol,
		}));
		const sorted = _.sortBy(selectOptions, ["text"]);
		return sorted;
	}, [preProcessedGeoJson]);

	// Preset list of possible zones this UI flow supports.
	const categoryNumberSelectOptions = useMemo(() => {
		const length = sortedAttrTableRecords.length;
		return Array.from({ length }, (_, i) => ({ key: String(i), value: String(i + 1), text: `${i + 1}-zone` }));
	}, [sortedAttrTableRecords]);

	// Initial DDL options for input of attribute values (IDs). Constrained to integers [0, numZones].
	const targetAttrValuesSelectIDOptions = useMemo(() => {
		if (!numZones) {
			return [];
		}
		return Array.from({ length: numZones + 1 }, (_, i) => ({
			key: i,
			value: String(i),
			text: String(i),
		}));
	}, [numZones]);

	// Initial DDL options for input of attribute values (suggested descriptions)
	const targetAttrValuesSelectDescrOptions = useMemo(() => {
		if (valuesMapping.length === 0) {
			return DEFAULT_ATTR_VALUE_DESCR_OPTIONS;
		}
		const userDescriptions = _.sortedUniq(
			_.sortBy(
				valuesMapping
					.filter(([, id, descr]) => id !== UNASSIGNED_ID && (descr ?? "").toLowerCase() !== UNASSIGNED_DESCR)
					.map(([, , descr]) => descr ?? "")
					.filter(Boolean),
			),
		);
		const userOptions = userDescriptions.map((descr, i) => ({ key: `user-descr-${i}`, value: descr, text: descr }));
		return [...DEFAULT_ATTR_VALUE_DESCR_OPTIONS, ...userOptions];
	}, [valuesMapping]);

	// Dynamic label above the attribute table
	const attributeAssignmentTooltip = useMemo(() => {
		return `aerialFARMS ID (integer: 0-${numZones}). 0 is a reserved value for '${UNASSIGNED_DESCR}'`;
	}, [numZones]);

	// Generate optional validation message and section to hi-light
	const validationMsgs = useMemo(() => {
		const msgs: [string, ValidationSection][] = [];

		// check uniqueness constraint of DataLayerTypeId x Num Zones for current data layer
		const isDuplicate = _.some(
			existingUploads,
			(existingUpload) =>
				existingUpload.id !== upload.id &&
				existingUpload.dataLayerTypeId === vectorDataType.id &&
				existingUpload.vectorDataNumZones === numZones,
		);
		if (isDuplicate) {
			msgs.push([
				`Data layer already has type: ${vectorDataTypeLabel} (${numZones} zone).`,
				ValidationSection.SelectCategoryNumber,
			]);
		}

		if (colNullsMap[selectedAttrCol]) {
			msgs.push([
				`Column "${selectedAttrCol}" contains missing values. Please select a different column or clean the dataset before continuing.`,
				ValidationSection.AttributeColumn,
			]);
		}

		const uniqStdIds = _.uniq(valuesMapping.map(([, stdId]) => stdId).filter((stdId) => stdId > 0));
		if (uniqStdIds.length != numZones) {
			const intro = uniqStdIds.length < numZones ? "Missing" : "Too many";
			msgs.push([
				`${intro} IDs: expected ${numZones} categories or zones, but ${uniqStdIds.length} unique IDs have been entered.`,
				ValidationSection.IdColumn,
			]);
		}

		return msgs;
	}, [
		valuesMapping,
		numZones,
		existingUploads,
		vectorDataType,
		vectorDataTypeLabel,
		upload.id,
		colNullsMap,
		selectedAttrCol,
	]);

	const fieldBoundaryColor = useMemo(
		() => resolveCssVarColor(FIELD_BOUNDARY_CSS_VAR, FIELD_BOUNDARY_FALLBACK_COLOR),
		[],
	);

	// Generate mapbox layer style props, conditional on which attribute table currently visualizing.
	const mapLayerStyles = useMemo(() => {
		const colorMatchExpr =
			mapAttributesTarget === AttrVis.STD
				? [
						"match",
						["get", "aerialfarmsStd"],
						..._.flatMap(Object.entries(targetAttrColors), ([id, color]) => [Number(id), color]),
						TRANSPARENT_COLOR,
					]
				: [
						"match",
						["get", "previewAttrsColorIdx"],
						..._.flatMap(sourceFeatureColors, (color, i) => [i, color]),
						TRANSPARENT_COLOR,
					];
		return [
			makeFeatureFillLayerProps("preview-attrs-layer", colorMatchExpr as DataDrivenPropertyValueSpecification<string>),
			makeFeatureOutlineLayerProps("preview-attrs-layer-outline", fieldBoundaryColor),
			makeValueLabelLayerProps("preview-attrs-value-labels"),
		] as const;
	}, [targetAttrColors, sourceFeatureColors, mapAttributesTarget, fieldBoundaryColor]);

	// Generate formatted string for feature count
	const featureCountFmt = useMemo(() => {
		const n = preprocessedLayerMetadata?.featureCount?.[LAYER_NUM];
		const result = n ? formatInt(n) : "";
		return result;
	}, [preprocessedLayerMetadata]);

	// Generate formatted string for plural label
	const fileNamesFmt = useMemo(() => {
		if (preprocessedLayerMetadata.fileNames.length > 1) {
			return "File Names";
		}
		return "File Name";
	}, [preprocessedLayerMetadata]);

	// Generate list of file names in vector data upload
	const fileNames = useMemo(() => {
		return preprocessedLayerMetadata.fileNames?.map((fileName) => <div key={fileName}>{fileName}</div>);
	}, [preprocessedLayerMetadata]);

	// Generate formatted string for geometry type(s) label
	const geometryTypesLabelFmt = useMemo(() => {
		if (preprocessedLayerMetadata.geometryTypes === undefined) {
			return "";
		}
		if (preprocessedLayerMetadata.geometryTypes[LAYER_NUM].length > 1) {
			return "Geometry Types";
		}
		return "Geometry Type";
	}, [preprocessedLayerMetadata]);

	// Generate formatted string listing geometry type(s)
	const geometryTypesFmt = useMemo(() => {
		if (preprocessedLayerMetadata.geometryTypes === undefined) {
			return "";
		}
		const geomTypes = preprocessedLayerMetadata.geometryTypes[LAYER_NUM];
		const fmt = geomTypes.length > 1 ? geomTypes.join(", ") : geomTypes[0];
		return fmt;
	}, [preprocessedLayerMetadata]);

	const autoFillDescriptionsDisabled = !isQuantitativeCol(selectedAttrCol) || !numZones || numZones < 2 || numZones > 7;

	/*
	 * Callbacks
	 */

	// Handle header sort events in the preview attributes table
	const handlePreviewAttrTableSort = useCallback(
		(clickedColumn: string) => {
			if (previewAttrTableSortColumn !== clickedColumn) {
				setPreviewAttrTableSortColumn(clickedColumn);
				setPreviewAttrTableSortColumnDir(false);
			} else {
				setPreviewAttrTableSortColumnDir((prev) => !prev);
			}
		},
		[previewAttrTableSortColumn],
	);

	// Handle header sort events in the config attributes table
	const handleConfigAttrTableSort = useCallback(
		(clickedColumn: string) => {
			const newColumn = clickedColumn as ConfigAttrTableColumn;
			if (configAttrTableSortColumn !== newColumn) {
				setConfigAttrTableSortColumn(newColumn);
				setConfigAttrTableSortColumnDir(false);
			} else {
				setConfigAttrTableSortColumnDir((prev) => !prev);
			}
		},
		[configAttrTableSortColumn],
	);

	// Handle mapbox library load event
	const handleMapLoad = useCallback(() => {
		if (mapRef?.current && preProcessedGeoJson.features.length) {
			const [minLng, minLat, maxLng, maxLat] = bbox(preProcessedGeoJson);
			mapRef.current.fitBounds(
				[
					[minLng, minLat],
					[maxLng, maxLat],
				],
				{ padding: 4, duration: 0 },
			);
		}
	}, [preProcessedGeoJson]);

	// Handle change in multi-selection of "Select attribute columns to preview in table"
	const handleMapAttributesTargetChange = useCallback(
		(_evt: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
			const newTargetAttrs = data.value as AttrVis;
			setMapAttributesTarget(newTargetAttrs);
		},
		[],
	);

	// Handle change in multi-selection of "Select attribute columns to preview in table"
	const handleSourceAttrColsChange = useCallback((_evt: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
		const newSelectedAttCols = data.value as string[];
		setSelectedSourceAttrCols(newSelectedAttCols);
	}, []);

	const handleAutoFillDescriptions = useCallback(() => {
		const presets = AUTO_FILL_DESCRIPTIONS[numZones];
		setAttrTableRecords((prev) =>
			prev.map((rec) => {
				if (rec.stdId === UNASSIGNED_ID) return rec;
				const descr = presets[rec.stdId - 1];
				if (!descr) return rec;
				return { ...rec, catDesc: descr };
			}),
		);
		setFormIsDirty(true);
	}, [numZones]);

	// Handle change in DDL "Select one attribute column to assign new values"
	const handleSelectedAttrColChange = useCallback(
		(_evt: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
			const newSelectedAttrCol = data.value as string;
			setSelectedAttrCol(newSelectedAttrCol);

			const sortedUniqueAttrVals = getSortedUniqueAttrVals(newSelectedAttrCol);
			const n = _.min([sortedUniqueAttrVals.length, MAX_ZONES]) ?? 1;
			const newWillDissolve = preProcessedGeoJson.features.length !== n;
			setStdGeoJsonUnedited(false);
			setAttrTableRecords(buildAttrTableRecords(newSelectedAttrCol, n, newWillDissolve));
			setNumZones(n);
			setFormIsDirty(true);
		},
		[getSortedUniqueAttrVals, buildAttrTableRecords, preProcessedGeoJson.features.length],
	);

	// Handle change in DDL "Select number of categories (zones) for attribute assignment"
	const handleSelectedNumZonesChange = useCallback((_evt: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
		const n = Number(data.value);
		setNumZones(n);
		// reset stdId/stdDesc for rows whose current ID exceeds the new zone count; preserve stdValue/stdArea
		setAttrTableRecords((prev) =>
			prev.map((rec) => {
				if (rec.stdId > n) {
					return { ...rec, stdId: UNASSIGNED_ID, catDesc: UNASSIGNED_DESCR };
				}
				return rec;
			}),
		);
		setFormIsDirty(true);
	}, []);

	// Handle change in the ID column of Categorical Assignment of attributes
	const handleAttrTableRecordsIDChange = useCallback(
		(srcVal: string | number | boolean, _evt: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
			const newId = Number(data.value);
			if (Number.isNaN(newId)) {
				return;
			}
			setStdGeoJsonUnedited(false);
			setAttrTableRecords((prev) => {
				let next = prev.map((rec) => {
					if (rec.attributeTableValue !== srcVal) return rec;
					let newDesc: string | undefined;
					if (newId === UNASSIGNED_ID) {
						newDesc = UNASSIGNED_DESCR;
					} else {
						// inherit description if this ID is already assigned elsewhere
						const match = prev.find((r) => r.stdId === newId && r.attributeTableValue !== srcVal);
						if (match) newDesc = match.catDesc;
					}
					return { ...rec, stdId: newId, catDesc: newDesc };
				});

				// enforce reserved "unassigned" description for ID 0 across all rows
				if (newId === UNASSIGNED_ID) {
					next = next.map((rec) => (rec.stdId === UNASSIGNED_ID ? { ...rec, catDesc: UNASSIGNED_DESCR } : rec));
				}

				return next;
			});
			setFormIsDirty(true);
		},
		[],
	);

	// Handle change in the Description column of Categorical Assignment of attributes table
	const handleAttrTableRecordsDescrChange = useCallback(
		(srcVal: string | number | boolean, _evt: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
			const rawDescr = typeof data.value === "string" ? data.value : String(data.value ?? "");
			const inputDescr = rawDescr.trim();
			if (!inputDescr) {
				return;
			}
			setAttrTableRecords((prev) => {
				const current = prev.find((r) => r.attributeTableValue === srcVal);
				if (!current) return prev;

				const currentStdId = current.stdId;
				let nextStdId = currentStdId;
				let nextDescr = inputDescr;

				// "unassigned" is a reserved descr, so id must be 0.
				if (inputDescr === UNASSIGNED_DESCR) {
					nextStdId = UNASSIGNED_ID;
					nextDescr = UNASSIGNED_DESCR;
				} else {
					// inherit ID if this description is already used elsewhere
					const match = prev.find((r) => r.catDesc === inputDescr && r.attributeTableValue !== srcVal);
					if (match) nextStdId = match.stdId;
				}

				let next = prev.map((rec) => {
					if (rec.attributeTableValue === srcVal) {
						return { ...rec, stdId: nextStdId, catDesc: nextDescr };
					}
					return rec;
				});

				// Keep descriptions consistent within an ID group when user edits the description for that group.
				if (nextStdId === currentStdId) {
					next = next.map((rec) => (rec.stdId === nextStdId ? { ...rec, catDesc: nextDescr } : rec));
				}

				// enforce reserved "unassigned" description for ID 0 across all rows
				if (nextStdId === UNASSIGNED_ID) {
					next = next.map((rec) => (rec.stdId === UNASSIGNED_ID ? { ...rec, catDesc: UNASSIGNED_DESCR } : rec));
				}

				return next;
			});
			setFormIsDirty(true);
		},
		[],
	);

	/**
	 * Send PUT request with new layer configuration info. Issue callback to parent component
	 * with response payload: Std GeoJson.
	 */
	const handleSaveRequest = useCallback(async () => {
		setIsSaving(true);

		const uuid = upload.id;
		const toastId = `vector-configurator-put-${uuid}`;
		toast.loading(`Processing vector data layer upload...`, { toastId, type: "info" });

		// let UI display toast before starting PUT request
		await new Promise<void>((resolve) => setTimeout(resolve, 1));

		const clientId = userAuth.currentClientId;
		const request: VectorDataLayerRequest = {
			uuid,
			layerNumber: 0,
			attributeFieldName: selectedAttrCol,
			numZones: numZones,
			valuesMapping,
		};
		try {
			const result = await updateVectorDataLayerUploadMutationAsync({
				getAccessToken: getTokenSilently,
				clientId,
				request,
			});
			toast.update(toastId, { render: "Done", type: "success", isLoading: false, autoClose: 1000 });
			onSaved(upload.id, result);
		} catch (error: unknown) {
			toast.dismiss(toastId);
			if (error instanceof ApiError && error.body?.messages) {
				const messages = error.body.messages as string[];
				toast.error(messages.join("; "));
			} else if (error instanceof Error) {
				toast.error(error.message);
			}
			console.error(error);
		}
		setIsSaving(false);
		setFormIsDirty(false);
	}, [
		upload.id,
		userAuth.currentClientId,
		selectedAttrCol,
		numZones,
		valuesMapping,
		updateVectorDataLayerUploadMutationAsync,
		getTokenSilently,
		onSaved,
	]);

	const handleValidationRequest = useCallback(() => {
		setIsValidationMsgShown(true);
	}, []);

	/*
	 * Rendering
	 */

	return (
		<>
			<Modal
				open={true}
				closeOnDimmerClick={false}
				closeOnEscape={false}
				size="fullscreen"
				className={styles.configuratorModal}
			>
				<Modal.Header className={styles.headerWithButtons}>
					<span>Vector Data Layer Configuration</span>
					<div>
						{validationMsgs.length > 0 && (
							<Button
								content="Validate Configuration"
								icon="warning"
								compact
								onClick={handleValidationRequest}
								disabled={validationMsgs.length > 0 && isValidationMsgShown}
							/>
						)}
						{validationMsgs.length === 0 && (
							<Button
								content="Save"
								icon={isSaving ? "spinner" : "checkmark"}
								compact
								positive
								onClick={() => withFieldAssociationConfirmation(() => void handleSaveRequest())}
								disabled={isSaving || !formIsDirty}
							/>
						)}
						<Button content="Cancel" icon="cancel" compact onClick={onCancel} negative />
					</div>
				</Modal.Header>
				<Modal.Content>
					<Form size="tiny" error={validationMsgs.length > 0 && isValidationMsgShown}>
						<Segment vertical basic>
							<SegmentGroup horizontal className={styles.segmentGroupHorizontal}>
								<Segment basic className={styles.infoSegment}>
									<Grid columns="2" divided="vertically" relaxed={false} padded={"horizontally"}>
										<Grid.Row>
											<Grid.Column width={4}>Vector Data Type</Grid.Column>
											<Grid.Column width={12}>{vectorDataTypeLabel}</Grid.Column>
										</Grid.Row>
										<Grid.Row>
											<Grid.Column width={4}>File Type</Grid.Column>
											<Grid.Column width={12}>{preprocessedLayerMetadata.vectorDataFormat}</Grid.Column>
										</Grid.Row>
										<Grid.Row>
											<Grid.Column width={4}>{fileNamesFmt}</Grid.Column>
											<Grid.Column width={12}>{fileNames}</Grid.Column>
										</Grid.Row>
										<Grid.Row>
											<Grid.Column width={4}>Feature Count</Grid.Column>
											<Grid.Column width={12}>{featureCountFmt}</Grid.Column>
										</Grid.Row>
										<Grid.Row>
											<Grid.Column width={4}>{geometryTypesLabelFmt}</Grid.Column>
											<Grid.Column width={12}>{geometryTypesFmt}</Grid.Column>
										</Grid.Row>
										<Grid.Row>
											<Grid.Column width={4}>Select Category Number</Grid.Column>
											<Grid.Column width={12}>
												<Tooltip overlay="Select number of categories (zones) for attribute assignment" placement="top">
													<Form.Select
														compact
														placeholder="Select # zones..."
														options={categoryNumberSelectOptions}
														value={String(numZones)}
														onChange={handleSelectedNumZonesChange}
														error={
															isValidationMsgShown &&
															validationMsgs.some(([, section]) => section === ValidationSection.SelectCategoryNumber)
														}
													/>
												</Tooltip>
											</Grid.Column>
										</Grid.Row>
									</Grid>
								</Segment>
								<Segment basic className={styles.mapSegment}>
									<div className={styles.mapContainer}>
										<div className={`${styles.mapOverlay} ${styles.mapOverlayTopleft}`}>
											<Form.Select
												compact
												options={MAP_VIS_ATTR_TARGET_OPTIONS}
												value={mapAttributesTarget}
												onChange={handleMapAttributesTargetChange}
											/>
										</div>
										<Map
											ref={mapRef}
											onLoad={handleMapLoad}
											projection="mercator"
											mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
											mapboxAccessToken={process.env.MAPBOX_ACCESS_TOKEN}
										>
											<Source type="geojson" data={previewGeoJson}>
												<Layer {...mapLayerStyles[0]} />
												<Layer {...mapLayerStyles[1]} />
												{mapAttributesTarget === AttrVis.STD && <Layer {...mapLayerStyles[2]} />}
											</Source>
										</Map>
									</div>
								</Segment>
							</SegmentGroup>
							{validationMsgs.length > 0 && isValidationMsgShown && (
								<>
									{validationMsgs.map(([msg]) => (
										<Message
											key={msg}
											content={msg}
											error
											compact
											icon="info circle"
											className={styles.validationMsg}
										/>
									))}
								</>
							)}
							<SegmentGroup horizontal className={styles.segmentGroupHorizontal}>
								<Segment basic className={styles.infoSegment}>
									<Header size="small">Preview Attribute Values From Data Source</Header>
									<div style={{ marginBottom: "1rem" }}>
										{topNGeoJsonFeaturesDescr}
										<Tooltip overlay="Select attribute columns to preview in table" placement="top">
											<Form.Dropdown
												fluid
												multiple
												selection
												search
												placeholder="Select attribute columns to preview in table"
												options={sourceAttrColsSelectOptions}
												value={selectedSourceAttrCols}
												onChange={handleSourceAttrColsChange}
											/>
										</Tooltip>
									</div>
									<div className={styles.tableScrollContainer}>
										<Table celled striped sortable selectable compact>
											<Table.Header>
												<Table.Row>
													<Table.HeaderCell></Table.HeaderCell>
													{selectedSourceAttrCols.map((attrCol) => (
														<Table.HeaderCell
															key={attrCol}
															collapsing
															className={isQuantitativeCol(attrCol) ? styles.tabularNumeric : styles.textCell}
															sorted={
																previewAttrTableSortColumn === attrCol
																	? previewAttrTableSortColumnDir
																		? "descending"
																		: "ascending"
																	: undefined
															}
															onClick={() => handlePreviewAttrTableSort(attrCol)}
														>
															{attrCol}
														</Table.HeaderCell>
													))}
												</Table.Row>
											</Table.Header>
											<Table.Body>
												{topNGeoJsonFeatures.map((feature) => (
													<Table.Row key={feature.properties.previewAttrsColorIdx}>
														<Table.Cell collapsing>
															<ColorChip bgColor={sourceFeatureColors[feature.properties.previewAttrsColorIdx]} />
														</Table.Cell>
														{selectedSourceAttrCols.map((attrCol) => {
															const raw = feature.properties[attrCol];
															const isNull = raw === null || raw === undefined;
															const decimals = colDecimalsMap[attrCol];
															const formatted =
																typeof raw === "number" && decimals !== undefined
																	? raw.toFixed(decimals)
																	: (raw as React.ReactNode);
															const display = isNull ? "null" : formatted;
															return (
																<Table.Cell
																	key={attrCol}
																	collapsing
																	warning={isNull}
																	className={isQuantitativeCol(attrCol) ? styles.tabularNumeric : styles.textCell}
																>
																	{display}
																</Table.Cell>
															);
														})}
													</Table.Row>
												))}
											</Table.Body>
										</Table>
									</div>
								</Segment>
								<Segment basic className={styles.infoSegment}>
									<Header size="small">
										{numZones}-zone Categorical Assignment of Attributes ({selectedAttrCol}&nbsp;&rarr;&nbsp;ID)
									</Header>
									<div className={styles.attrColSelectRow}>
										<Tooltip overlay="Select one attribute column to assign new values" placement="top">
											<Dropdown
												fluid
												selection
												search
												placeholder="Select one attribute column to assign new values"
												options={sourceAttrColsSelectOptions}
												value={selectedAttrCol}
												onChange={handleSelectedAttrColChange}
												error={
													isValidationMsgShown &&
													validationMsgs.some(([, section]) => section === ValidationSection.AttributeColumn)
												}
											/>
										</Tooltip>

										<Tooltip
											overlay={
												autoFillDescriptionsDisabled
													? "Auto-fill zone descriptions (enabled for quantitative attributes only)"
													: "Auto-fill zone descriptions based on number of zones"
											}
											placement="top"
										>
											<div style={{ flexShrink: 0 }}>
												<Button
													icon
													labelPosition="right"
													onClick={handleAutoFillDescriptions}
													disabled={autoFillDescriptionsDisabled}
												>
													<Icon className="magic" />
													Auto-fill Descriptions
												</Button>
											</div>
										</Tooltip>
									</div>

									<div className={styles.tableScrollContainer}>
										<Table celled striped sortable selectable compact textAlign="center">
											<Table.Header>
												<Table.Row>
													<Table.HeaderCell> </Table.HeaderCell>
													<Tooltip overlay={"Source attribute values from selected column"} placement="top">
														<Table.HeaderCell
															className={selectedAttrColIsQuant ? styles.tabularNumeric : styles.textCell}
															onClick={() => handleConfigAttrTableSort(ConfigAttrTableColumn.SRC_VALS)}
															sorted={
																configAttrTableSortColumn === ConfigAttrTableColumn.SRC_VALS
																	? configAttrTableSortColumnDir
																		? "descending"
																		: "ascending"
																	: undefined
															}
														>
															{selectedAttrCol}
														</Table.HeaderCell>
													</Tooltip>
													<Tooltip overlay={attributeAssignmentTooltip} placement="top">
														<Table.HeaderCell
															onClick={() => handleConfigAttrTableSort(ConfigAttrTableColumn.STD_IDS)}
															sorted={
																configAttrTableSortColumn === ConfigAttrTableColumn.STD_IDS
																	? configAttrTableSortColumnDir
																		? "descending"
																		: "ascending"
																	: undefined
															}
														>
															ID
														</Table.HeaderCell>
													</Tooltip>
													<Table.HeaderCell
														onClick={() => handleConfigAttrTableSort(ConfigAttrTableColumn.STD_DESCR)}
														sorted={
															configAttrTableSortColumn === ConfigAttrTableColumn.STD_DESCR
																? configAttrTableSortColumnDir
																	? "descending"
																	: "ascending"
																: undefined
														}
													>
														<span className={styles.cursorHelp}>
															{hasEstimatedValues &&
																!attrTableRecords.some((r) => r.catDesc && r.catDesc !== UNASSIGNED_DESCR) && (
																	<Tooltip
																		overlay="Descriptions will be updated to each zone's actual values after save."
																		placement="top"
																	>
																		<Icon name="info circle" className={styles.infoIconHelpLeft} />
																	</Tooltip>
																)}
															Description
														</span>
													</Table.HeaderCell>
													<Table.HeaderCell
														className={selectedAttrColIsQuant ? styles.tabularNumeric : styles.textCell}
														onClick={() => handleConfigAttrTableSort(ConfigAttrTableColumn.STD_VALUE)}
														sorted={
															configAttrTableSortColumn === ConfigAttrTableColumn.STD_VALUE
																? configAttrTableSortColumnDir
																	? "descending"
																	: "ascending"
																: undefined
														}
													>
														{hasEstimatedValues ? "Value (Est.)" : "Value"}
													</Table.HeaderCell>
													<Table.HeaderCell
														className={styles.tabularNumeric}
														onClick={() => handleConfigAttrTableSort(ConfigAttrTableColumn.STD_AREA)}
														sorted={
															configAttrTableSortColumn === ConfigAttrTableColumn.STD_AREA
																? configAttrTableSortColumnDir
																	? "descending"
																	: "ascending"
																: undefined
														}
													>
														{hasEstimatedAreas ? "Acres (Est.)" : "Acres"}
													</Table.HeaderCell>
												</Table.Row>
											</Table.Header>
											<Table.Body>
												{sortedAttrTableRecords.map((rec) => (
													<Table.Row key={String(rec.attributeTableValue)}>
														<Table.Cell collapsing>
															{targetAttrColors[rec.stdId] === TRANSPARENT_COLOR ? (
																<TransparentColorChip />
															) : (
																<ColorChip bgColor={targetAttrColors[rec.stdId] || "grey"} />
															)}
														</Table.Cell>
														<Table.Cell
															warning={rec.attributeTableValue === null || rec.attributeTableValue === undefined}
															className={(() => {
																if (selectedAttrColIsQuant) return styles.tabularNumeric;
																const zone = zoneDisplayValues[rec.stdId];
																if (zone?.valueIsEstimate || zone?.areaIsEstimate) {
																	return styles.textCellWithIcon;
																}
																return styles.textCell;
															})()}
														>
															{(() => {
																const isNull =
																	rec.attributeTableValue === null || rec.attributeTableValue === undefined;
																if (isNull) return "null";
																const numDecimals = colDecimalsMap[selectedAttrCol];
																const fmtValue = numDecimals
																	? (rec.attributeTableValue as number).toFixed(numDecimals)
																	: rec.attributeTableValue;
																if (!rec.estimateTooltip) {
																	return fmtValue;
																}
																if (selectedAttrColIsQuant) {
																	// column is right aligned, so render info icon to left
																	return (
																		<Tooltip overlay={rec.estimateTooltip} placement="top">
																			<span className={styles.cursorHelp}>
																				<Icon name="info circle" className={styles.infoIconHelpLeft} />
																				{fmtValue}
																			</span>
																		</Tooltip>
																	);
																} else {
																	// column is left aligned, so render info icon to right
																	return (
																		<Tooltip overlay={rec.estimateTooltip} placement="top">
																			<span className={styles.cursorHelp}>
																				{fmtValue}
																				<Icon name="info circle" className={styles.infoIconHelpRight} />
																			</span>
																		</Tooltip>
																	);
																}
															})()}
														</Table.Cell>
														<Table.Cell collapsing>
															<Form.Dropdown
																compact
																fluid
																selection
																options={targetAttrValuesSelectIDOptions}
																value={String(rec.stdId)}
																onChange={(e, data) => handleAttrTableRecordsIDChange(rec.attributeTableValue, e, data)}
																error={
																	isValidationMsgShown &&
																	validationMsgs.some(([, section]) => section === ValidationSection.IdColumn)
																}
															/>
														</Table.Cell>
														<Table.Cell>
															<Form.Dropdown
																compact
																fluid
																search
																selection
																allowAdditions
																options={targetAttrValuesSelectDescrOptions}
																placeholder={rec.catDesc ? undefined : rec.descPlaceholder}
																value={rec.catDesc ?? ""}
																onChange={(e, data) =>
																	handleAttrTableRecordsDescrChange(rec.attributeTableValue, e, data)
																}
															/>
														</Table.Cell>
														<Table.Cell className={selectedAttrColIsQuant ? styles.tabularNumeric : styles.textCell}>
															{zoneDisplayValues[rec.stdId]?.valueIsEstimate ? "~" : ""}
															{zoneDisplayValues[rec.stdId]?.value ?? ""}
														</Table.Cell>
														<Table.Cell className={styles.tabularNumeric}>
															{zoneDisplayValues[rec.stdId]?.areaIsEstimate ? "~" : ""}
															{zoneDisplayValues[rec.stdId]?.areaAcres.toFixed(2) ?? ""}
														</Table.Cell>
													</Table.Row>
												))}
											</Table.Body>
										</Table>
									</div>
								</Segment>
							</SegmentGroup>
						</Segment>
					</Form>
				</Modal.Content>
			</Modal>

			<SimpleOkCancelModal
				headerMsg={FIELD_ASSOC_RESET_HEADER}
				bodyMsg={FIELD_ASSOC_RESET_BODY}
				open={fieldAssocModalIsOpen}
				onOk={handleFieldAssocModalOk}
				onCancel={handleFieldAssocModalCancel}
			/>
		</>
	);
};
export default VectorConfiguratorModal;
